import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '../config/api';

const AuthContext = createContext();

export function formatApiErrorDetail(detail) {
  if (detail === null || detail === undefined) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const register = useCallback(async (email, password, name) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Check if email not found (404 status)
      if (error.response?.status === 404 && detail === "EMAIL_NOT_FOUND") {
        return { success: false, error: detail, errorType: 'EMAIL_NOT_FOUND' };
      }
      return { success: false, error: formatApiErrorDetail(detail) || error.message };
    }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  }, []);

  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(false);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Logout error:', error);
    }
  }, []);

  const contextValue = useMemo(() => ({ 
    user, 
    loading, 
    register, 
    login, 
    logout, 
    forgotPassword, 
    resetPassword 
  }), [
    user, 
    loading, 
    register, 
    login, 
    logout, 
    forgotPassword, 
    resetPassword
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
